import * as React from "react"
import { UtensilsCrossedIcon } from "lucide-react"

import { cn } from "@workspace/ui/lib/utils"

/**
 * Restaurant-flavored loading spinner (shared widget). A plate-shaped ring — a
 * primary-tinted conic-gradient arc that fades to transparent as it spins,
 * masked to a thin band — cradling a crossed-utensils glyph that gently pulses.
 * Reads as "food is coming", not a generic throbber. The ring is built from the
 * tenant's own `--primary` token via `color-mix`, so it renders in each tenant's
 * hue (never a hardcoded color).
 *
 * Server-safe: no hooks, no `"use client"`. Direction-agnostic (centered, no
 * LTR/RTL conditionals). `motion-reduce`-safe — both animations disable under
 * reduced motion while the accessible `label` still announces the busy state.
 */
function Loader({
  size = 40,
  label = "Loading",
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "children"> & {
  size?: number
  label?: string
}) {
  const band = Math.max(2, Math.round(size / 14))
  const iconSize = Math.round(size * 0.42)

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={cn("relative inline-grid place-items-center", className)}
      style={{ width: size, height: size }}
      {...props}
    >
      {/* Spinning plate ring: a fading conic arc masked to a thin band. */}
      <span
        aria-hidden="true"
        className="col-start-1 row-start-1 animate-spin rounded-full motion-reduce:animate-none"
        style={{
          width: size,
          height: size,
          background:
            "conic-gradient(from 90deg, transparent 0deg, color-mix(in oklab, var(--primary), transparent 60%) 140deg, var(--primary) 320deg)",
          WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${band}px), #000 calc(100% - ${band}px))`,
          mask: `radial-gradient(farthest-side, transparent calc(100% - ${band}px), #000 calc(100% - ${band}px))`,
        }}
      />

      <UtensilsCrossedIcon
        aria-hidden="true"
        strokeWidth={1.75}
        style={{ width: iconSize, height: iconSize }}
        className="col-start-1 row-start-1 animate-pulse text-primary motion-reduce:animate-none"
      />

      <span className="sr-only">{label}</span>
    </div>
  )
}

export { Loader }
